import { useState, useEffect } from 'react';
import { Accessibility, X, Type, Contrast, Underline, Sun, Moon, RotateCcw } from 'lucide-react';

type A11ySettings = {
  fontSize: 'normal' | 'large' | 'x-large';
  contrast: 'normal' | 'high';
  focusVisible: boolean;
  reduceMotion: boolean;
};

const STORAGE_KEY = 'a11y-settings';

const defaultSettings: A11ySettings = {
  fontSize: 'normal',
  contrast: 'normal',
  focusVisible: false,
  reduceMotion: false,
};

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<A11ySettings>(defaultSettings);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setSettings(JSON.parse(saved));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const html = document.documentElement;

    // Font size
    html.style.fontSize =
      settings.fontSize === 'x-large' ? '18px' :
      settings.fontSize === 'large' ? '16.5px' : '';

    // High contrast
    if (settings.contrast === 'high') {
      html.classList.add('a11y-high-contrast');
    } else {
      html.classList.remove('a11y-high-contrast');
    }

    // Focus visible
    if (settings.focusVisible) {
      html.classList.add('a11y-focus-visible');
    } else {
      html.classList.remove('a11y-focus-visible');
    }

    // Reduce motion
    if (settings.reduceMotion) {
      html.classList.add('a11y-reduce-motion');
    } else {
      html.classList.remove('a11y-reduce-motion');
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const update = (patch: Partial<A11ySettings>) => {
    setSettings(prev => ({ ...prev, ...patch }));
  };

  const reset = () => {
    setSettings(defaultSettings);
  };

  return (
    <>
      {/* Floating Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-6 left-6 z-50 w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFDD00] ${
          open ? 'bg-[#0057B7] text-white' : 'bg-[#FFDD00] text-[#0057B7]'
        }`}
        aria-label={open ? 'Close accessibility options' : 'Open accessibility options'}
        aria-expanded={open}
        style={{ bottom: '6rem' }}
      >
        {open ? <X className="w-5 h-5" /> : <Accessibility className="w-5 h-5" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="Accessibility options"
          className="fixed bottom-[11rem] left-6 z-50 w-72 bg-[#0057B7] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Accessibility className="w-4 h-4 text-[#FFDD00]" />
              <span className="text-sm font-semibold text-white">Accessibility</span>
            </div>
            <button
              onClick={reset}
              className="flex items-center gap-1 text-xs text-white/40 hover:text-[#0057B7] transition-colors"
              title="Reset all settings"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>

          {/* Options */}
          <div className="p-4 space-y-4">

            {/* Font Size */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Type className="w-3.5 h-3.5 text-white/40" />
                <span className="text-xs font-medium text-white/60">Font Size</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {(['normal', 'large', 'x-large'] as const).map(size => (
                  <button
                    key={size}
                    onClick={() => update({ fontSize: size })}
                    className={`py-2 px-1 text-xs rounded-lg border transition-colors ${
                      settings.fontSize === size
                        ? 'bg-[#FFDD00] border-[#FFDD00] text-[#0057B7] font-medium'
                        : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {size === 'normal' ? 'A' : size === 'large' ? 'A+' : 'A++'}
                  </button>
                ))}
              </div>
            </div>

            {/* Contrast */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Contrast className="w-3.5 h-3.5 text-white/40" />
                <span className="text-xs font-medium text-white/60">Contrast</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => update({ contrast: 'normal' })}
                  className={`py-2 px-1 text-xs rounded-lg border transition-colors flex items-center justify-center gap-1.5 ${
                    settings.contrast === 'normal'
                      ? 'bg-[#FFDD00] border-[#FFDD00] text-[#0057B7] font-medium'
                      : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Sun className="w-3 h-3" /> Normal
                </button>
                <button
                  onClick={() => update({ contrast: 'high' })}
                  className={`py-2 px-1 text-xs rounded-lg border transition-colors flex items-center justify-center gap-1.5 ${
                    settings.contrast === 'high'
                      ? 'bg-[#FFDD00] border-[#FFDD00] text-[#0057B7] font-medium'
                      : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Moon className="w-3 h-3" /> High
                </button>
              </div>
            </div>

            {/* Toggles */}
            <div className="space-y-2">
              <button
                onClick={() => update({ focusVisible: !settings.focusVisible })}
                className={`w-full flex items-center justify-between py-2.5 px-3 rounded-lg border transition-colors ${
                  settings.focusVisible
                    ? 'bg-[#0057B7]/20 border-[#0057B7]/30'
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Underline className="w-3.5 h-3.5 text-white/40" />
                  <span className="text-xs text-white/70">Focus Outlines</span>
                </div>
                <span className={`w-8 h-4 rounded-full relative transition-colors ${
                  settings.focusVisible ? 'bg-[#0057B7]' : 'bg-white/20'
                }`}>
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                    settings.focusVisible ? 'left-4.5' : 'left-0.5'
                  }`} style={{ left: settings.focusVisible ? '18px' : '2px' }} />
                </span>
              </button>

              <button
                onClick={() => update({ reduceMotion: !settings.reduceMotion })}
                className={`w-full flex items-center justify-between py-2.5 px-3 rounded-lg border transition-colors ${
                  settings.reduceMotion
                    ? 'bg-[#0057B7]/20 border-[#0057B7]/30'
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-2">
                  <RotateCcw className="w-3.5 h-3.5 text-white/40" />
                  <span className="text-xs text-white/70">Reduce Motion</span>
                </div>
                <span className={`w-8 h-4 rounded-full relative transition-colors ${
                  settings.reduceMotion ? 'bg-[#0057B7]' : 'bg-white/20'
                }`}>
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all`} style={{ left: settings.reduceMotion ? '18px' : '2px' }} />
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
